import Select from "react-select";
import React, { Fragment, useEffect, useState, useRef } from "react";
import {
  AddIcon,
  FilterIcon,
  EditIcon,
  DeleteIcon,
  ResetIcon,
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  ApproveIcon,
  Disapprove,
  CSVIcon,
} from "../../../assets/icons/index";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Pagination from "../../shared/common/pagination";
import Constant from "../../../utils/constant";
import DeleteModal from "../../shared/common/deleteModal";
import Loader from "../../shared/loader/loader";
import Tooltip from "../../shared/common/ellipsisText";
import { Link, useNavigate } from "react-router";
import { IWorksheet } from "../../../model/interface/worksheet";
import ExportToCSV from "../../shared/common/exportToCsv";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import { Menu, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import { TagColor } from "../../../model/tagColor";
import TagSelector, { TagSelectorMode } from "../../shared/common/tagSelector";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";
import MultiStateButton from "../../shared/common/multiStateButton";
import { ISelectElement } from "../../../model/interface/keyValuePair";
import { getContentArea } from "../../../api/contentArea.service";

const activeMultiState = [
  { label: "Active", value: true },
  { label: "Both", value: null, selected: true },
  { label: "Inactive", value: false },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
type IInitialFilter = {
  worksheetId: number;
  standardId: number;
  standardName: string;
  title: string;
  description: string;
  isActive: boolean | undefined;
  contentAreaId: number;
  gradeLevelId: string;
};
const Worksheets = () => {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const initialFilter: IInitialFilter = {
    worksheetId: 0,
    standardId: 0,
    standardName: "",
    title: "",
    description: "",
    isActive: undefined,
    contentAreaId: 0,
    gradeLevelId: "",
  };
  const navigate = useNavigate();
  const [worksheets, setWorksheets] = useState<IWorksheet[]>([]);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [resetDropdownFlag, setResetDropdownFlag] = useState<boolean>(false);
  const [worksheetDelete, setWorksheetDelete] = useState<number>(0);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [orderByColumn, setOrderByColumn] = useState<string>("worksheetId");
  const [sortDir, setSortDir] = useState<string>("asc");
  const [loading, setLoading] = useState<boolean>(false);
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [filterInput, setFilterInput] = useState<IInitialFilter>(initialFilter);
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const [selectedTagIds, setSelectedTagIds] = useState<Array<number>>([]);
  const childRef: any = useRef(undefined);
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined);
  const [contentAreas, setContentAreas] = useState<ISelectElement[]>([]);
  const gradeLevelData = [
    {
      value: "ece",
      name: "Early Childhood Learning",
      label: "Early Childhood Learning (ece)",
    },
    { value: "elem", name: "Elementary", label: "Elementary (elem)" },
    { value: "ms", name: "Middle School", label: "Middle School (ms)" },
    { value: "hs", name: "High School", label: "High School (hs)" },
  ];

  async function bindContentAreas() {
    const response = await getContentArea();
    const result = response.data.data;
    const contentAreaData = result.map((item) => {
      return { label: item.name, value: item.contentAreaId };
    });
    setContentAreas(contentAreaData);
  }

  function handleSort(columnName) {
    setOrderByColumn(columnName);
    setSortAscending(!sortAscending);
    setSortDir(sortAscending ? "DESC" : "ASC");
  }

  async function bindWorksheets(resetObj?, isFilter?) {
    setLoading(true);

    if (isFilter) setIsFilter(true);

    const worksheet: IWorksheet = {
      worksheetId: resetObj ? 0 : filterInput.worksheetId,
      standardName: resetObj ? "" : filterInput.standardName,
      standardCode: "",
      title: resetObj ? "" : filterInput.title,
      description: resetObj ? "" : filterInput.description,
      spanishTitle: "",
      spanishDescription: "",
      standardId: resetObj ? 0 : filterInput.standardId,
      isActive: resetObj ? undefined : isActive,
      userId: 0,
      domainId: 0,
      subjectId: 0,
      contentAreaId: filterInput.contentAreaId,
      worksheetTags: selectedTagIds.toString(),
      gradeLevelId: filterInput.gradeLevelId,
      gradeLevel: "",
    };

    const service = await import("../../../api/worksheets.service");
    service
      .getWorksheets(
        worksheet,
        pageSize,
        isFilter ? 1 : pageNumber,
        orderByColumn,
        sortDir
      )
      .then((response: any) => {
        setWorksheets(response.data.data);
        setTotalPages(Math.ceil(response.data.totalRecords / defaultPageSize));
        setTotalRecords(response.data.totalRecords);
        if (isFilter) {
          paginationRef?.current?.onReset();
          pageSizeRef?.current?.onReset();
          setIsFilter(false);
        }
        setLoading(false);
      });
  }

  const resetFilterHandler = () => {
    setResetDropdownFlag(!resetDropdownFlag);
    setFilterInput(initialFilter);
    bindWorksheets(true, false);
    paginationRef?.current?.onReset();
    pageSizeRef?.current?.onReset();
    handleUntagged();
  };

  function confirmDelete(idToDelete: number) {
    if (idToDelete) {
      setShowDeletePopup(true);
      setWorksheetDelete(idToDelete);
    }
  }

  async function deleteRecord() {
    const worksheet = worksheets.filter(
      (w) => w.worksheetId == worksheetDelete
    )[0];

    const service = await import("../../../api/worksheets.service");
    service
      .deleteWorksheet(
        worksheetDelete,
        worksheet?.worksheetFilePath,
        worksheet?.worksheetKeyFilePath
      )
      .then(() => {
        setShowDeletePopup(false);
        setWorksheetDelete(0);
        resetFilterHandler();
      });
  }

  function closeDeletePopup() {
    setShowDeletePopup(false);
    setWorksheetDelete(0);
  }

  function handleFilterChange(e: any, type) {
    switch (type) {
      case "StandardName":
        setFilterInput({
          ...filterInput,
          standardName: e.target.value || "",
        });
        break;
      case "Description":
        setFilterInput({
          ...filterInput,
          description: e.target.value || "",
        });
        break;
      case "StandardId":
        setFilterInput({
          ...filterInput,
          standardId: e.value || 0,
        });
        break;
      case "Title":
        setFilterInput({
          ...filterInput,
          title: e.target.value || "",
        });
        break;
      case "GradeLevel":
        setFilterInput({
          ...filterInput,
          gradeLevelId: e.map((item) => item.value).join(", ") || "",
        });
        break;
      case "ContentArea":
        setFilterInput({
          ...filterInput,
          contentAreaId: e.value || 0,
        });
        break;
    }
  }

  const handleRedirect = (id?: number | string) => {
    navigate(
      id
        ? Constant.WORKSHEETEDIT.replace(":id", id.toString())
        : Constant.ADDWORKSHEET,
      { state: { idToEdit: id } }
    );
  };

  useEffect(() => {
    bindContentAreas();
  }, []);

  useEffect(() => {
    bindWorksheets();
  }, [pageNumber]);

  useEffect(() => {
    bindWorksheets();
  }, [selectedTagIds]);

  useEffect(() => {
    if (pageNumber === 1 && !isFilter) {
      bindWorksheets();
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  useEffect(() => {
    bindWorksheets();
  }, [sortDir]);

  function handleStandardIdChange(e: any) {
    setFilterInput({
      ...filterInput,
      standardId: e.target.value || 0,
    });
  }

  function handleWorksheetIdChange(e: any) {
    setFilterInput({
      ...filterInput,
      worksheetId: e.target.value || 0,
    });
  }

  const filterHandler = (e) => {
    e.preventDefault();
    bindWorksheets(false, true);
  };

  /** Code to implement input type number to support all browsers */
  useEffect(() => {
    const input = document.getElementById("standardId");
    if (input) {
      input.addEventListener("keypress", (e: any) => {
        const keyValue = e.key;
        if (!e.target.value && e.key == 0) {
          e.preventDefault();
        } else {
          if (!/[0-9.]/.test(keyValue)) {
            e.preventDefault();
          }
        }
      });
    }
  }, []);

  const exportFn = async () => {
    setLoading(true);

    const worksheet: IWorksheet = {
      worksheetId: 0,
      standardName: filterInput.standardName,
      title: filterInput.title,
      description: filterInput.description,
      spanishTitle: "",
      spanishDescription: "",
      standardId: filterInput.standardId,
      isActive: true,
      userId: 0,
      domainId: 0,
      subjectId: 0,
      standardCode: "",
      worksheetTags: selectedTagIds.toString(),
      gradeLevelId: filterInput.gradeLevelId,
      contentAreaId: filterInput.contentAreaId,
      gradeLevel: "",
    };

    const service = await import("../../../api/worksheets.service");
    const response = service.getWorksheets(
      worksheet,
      9999,
      1,
      orderByColumn,
      sortDir
    );
    const data = (await response).data;
    setLoading(false);

    if (data && data.data && data.data.length > 0) {
      childRef?.current?.onDownload(data.data, "worksheet.csv");
    } else {
      toast.warning("No records to download");
    }
  };

  function redirectToStandardDetail(standardId) {
    navigate(Constant.EDITSTANDARD.replace(":id", standardId.toString()));
  }

  const downloadWS = async (id, worksheet) => {
    const service = await import("../../../api/worksheets.service");
    service.getWorksheetPreviewURL(id, worksheet).then((response) => {
      const file = response.data;
      const link = document.createElement("a");
      link.href = file;
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
    });
  };

  const handleFilterTags = (tags) => {
    const selectedTagIds = tags
      .filter((tag) => tag.selected === true)
      .map((tag) => tag.tagId);
    setSelectedTagIds(selectedTagIds);
  };

  const handleTagClick = (tag) => {
    setSelectedTagIds([tag.tagId]);
  };

  const handleUntagged = () => {
    setSelectedTagIds([]);
  };

  return (
    <AllowedTo perform={Permissions.worksheet_view} no={() => <AccessDenied />}>
      {loading && <Loader />}

      {showDeletePopup && (
        <DeleteModal
          closeDeleteModal={closeDeletePopup}
          deleteRecord={deleteRecord}
        />
      )}

      <div className="flex-1">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-end justify-between">
              <h3 className="text-lg font-light text-gray-900">Worksheets</h3>
              <div>
                <AllowedTo perform={Permissions.worksheet_create}>
                  <button
                    onClick={() => handleRedirect()}
                    type="button"
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 rounded-md shadow-xs text-white dark:text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color hover:shadow-lg transition ease-in-out duration-300"
                  >
                    <AddIcon className="w-5"></AddIcon>
                    Add Worksheet
                  </button>
                </AllowedTo>
              </div>
            </div>

            <form action="#" onSubmit={filterHandler}>
              <div className="pt-6 border-b pb-6">
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-3 sm:col-span-3  ">
                    <label className="text-sm text-gray-700">
                      Worksheet Id
                    </label>
                    <div className="contentArea mt-1">
                      <input
                        id="standardId"
                        name="standardId"
                        value={filterInput?.worksheetId || ""}
                        onChange={handleWorksheetIdChange}
                        type="text"
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      />
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-3  ">
                    <label className="text-sm text-gray-700">Standard Id</label>
                    <div className="contentArea mt-1">
                      <input
                        id="standardId"
                        name="standardId"
                        value={filterInput?.standardId || ""}
                        onChange={handleStandardIdChange}
                        type="text"
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="col-span-3 sm:col-span-3  ">
                    <label className="text-sm text-gray-700">
                      Standard Name
                    </label>
                    <div className="contentArea mt-1">
                      <input
                        id="standardName"
                        name="standardName"
                        value={filterInput.standardName}
                        onChange={(e) => {
                          handleFilterChange(e, "StandardName");
                        }}
                        type="text"
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      />
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-3  ">
                    <label className="text-sm text-gray-700">Title</label>
                    <div className="contentArea mt-1">
                      <input
                        id="title"
                        name="title"
                        value={filterInput.title}
                        onChange={(e) => {
                          handleFilterChange(e, "Title");
                        }}
                        type="text"
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      />
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-3">
                    <label className="text-sm text-gray-700">Description</label>
                    <div className="contentArea mt-1">
                      <input
                        id="description"
                        name="description"
                        value={filterInput.description}
                        onChange={(e) => {
                          handleFilterChange(e, "Description");
                        }}
                        type="text"
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      />
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center mt-6">
                    <MultiStateButton
                      labels={activeMultiState}
                      onSelect={(e) => setIsActive(e.value)}
                      onReset={resetDropdownFlag}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-1 flex items-end">
                    <div className="flex items-center contentArea">
                      <TagSelector
                        label={"Tags"}
                        onTagSelectChange={(tags) => {
                          handleFilterTags(tags);
                        }}
                        onFilterUntagged={() => {
                          handleUntagged();
                        }}
                        removeTag={(tags, tag) => {
                          handleFilterTags(tags);
                        }}
                        mode={TagSelectorMode.Filter}
                        categoryId={0}
                        selectedTagIds={selectedTagIds}
                      />
                    </div>
                  </div>

                  <div className="col-span-3 sm:col-span-3  ">
                    <label className="text-sm text-gray-700">Grade Level</label>
                    <Select
                      isMulti={true}
                      className="mt-1 text-sm"
                      options={gradeLevelData}
                      onChange={(e) => {
                        handleFilterChange(e, "GradeLevel");
                      }}
                      theme={(theme) => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary25: "#B3B3B3",
                          primary: "#041E42",
                        },
                      })}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          background:
                            document.getElementsByClassName("dark")?.length > 0
                              ? "#1A1C23"
                              : "white",
                          borderColor:
                            document.getElementsByClassName("dark")?.length > 0
                              ? "#6B7280"
                              : "#D1D5DB",
                          borderWidth: "1px",
                          "&:hover": {
                            borderColor:
                              state.isFocused &&
                              document.getElementsByClassName("dark")?.length >
                                0
                                ? "#6b7280"
                                : state.isFocused
                                ? "#4c5563"
                                : "",
                          },
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color:
                            document.getElementsByClassName("dark")?.length > 0
                              ? "white"
                              : "#374051",
                        }),
                        input: (base) => ({
                          ...base,
                          color:
                            document.getElementsByClassName("dark")?.length > 0
                              ? "white"
                              : "",
                        }),
                      }}
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-3">
                    <label className="text-sm text-gray-700">
                      Content Area
                    </label>
                    <Select
                      options={contentAreas}
                      className="mt-1 text-sm"
                      isSearchable={true}
                      onChange={(e) => {
                        handleFilterChange(e, "ContentArea");
                      }}
                      theme={(theme) => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary25: "#B3B3B3",
                          primary: "#041E42",
                        },
                      })}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          background:
                            document.getElementsByClassName("dark")?.length > 0
                              ? "#1A1C23"
                              : "white",
                          borderColor:
                            document.getElementsByClassName("dark")?.length > 0
                              ? "#6B7280"
                              : "#D1D5DB",
                          borderWidth: "1px",
                          "&:hover": {
                            borderColor:
                              state.isFocused &&
                              document.getElementsByClassName("dark")?.length >
                                0
                                ? "#6b7280"
                                : state.isFocused
                                ? "#4c5563"
                                : "",
                          },
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color:
                            document.getElementsByClassName("dark")?.length > 0
                              ? "white"
                              : "#374051",
                        }),
                        input: (base) => ({
                          ...base,
                          color:
                            document.getElementsByClassName("dark")?.length > 0
                              ? "white"
                              : "",
                        }),
                      }}
                    />
                  </div>
                </div>
                <div>
                  <button
                    type="submit"
                    className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  >
                    <FilterIcon className="w-3 h-3"></FilterIcon>
                    <span>Filter</span>
                  </button>
                  <button
                    type="button"
                    className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                    onClick={resetFilterHandler}
                  >
                    <ResetIcon className="w-3 h-3"></ResetIcon>
                    <span>Reset Filter</span>
                  </button>
                </div>
              </div>

              <div>
                <div className="flex justify-start">
                  <button
                    type="button"
                    onClick={() => exportFn()}
                    className="mt-4 py-1.5 px-4 inline-flex ml-auto items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                  >
                    <CSVIcon className="w-3 h-3 dark:text-white stroke-current"></CSVIcon>
                    <span>Export</span>
                  </button>
                  <ExportToCSV ref={childRef} />
                </div>
              </div>
            </form>

            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="px-5 w-8 py-2 pl-6 font-light text-sm text-left whitespace-nowrap text-white dark:text-white tracking-wider rounded-tl-lg"
                            >
                              <span className="flex">
                                Id
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "worksheetId" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("worksheetId")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("worksheetId")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-wrap text-sm text-left text-white  dark:text-white "
                            >
                              <span className="flex gap-2">
                                Title
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "title" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("title")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("title")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2 whitespace-nowrap">
                                Standard Id
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "standardId" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("standardId")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("standardId")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2 whitespace-nowrap">
                                Standard Code
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "standardCode" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("standardCode")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("standardCode")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2 whitespace-nowrap">
                                Standard Name
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "standardName" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("standardName")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("standardName")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-wrap text-sm text-left text-white dark:text-white  "
                            >
                              Description
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-wrap text-sm text-left text-white dark:text-white  "
                            >
                              File Name
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-wrap text-sm text-left text-white dark:text-white  "
                            >
                              Key File Name
                            </th>

                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider rounded-tr-lg"
                            ></th>
                          </tr>
                        </thead>
                        <tbody>
                          {worksheets.map((worksheet: IWorksheet, index) => (
                            <>
                              <tr
                                key={worksheet.worksheetId}
                                className={
                                  index % 2 === 0 ? "bg-white" : "bg-gray-100"
                                }
                              >
                                <td className="px-3 py-2  pl-6 whitespace-wrap text-sm text-left font-normal text-gray-500 relative">
                                  {worksheet.isActive ? (
                                    <span className="mr-1 mt-2 h-full w-0.5 bg-green-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                                  ) : (
                                    <span className="mr-1 mt-2 h-full w-0.5 bg-red-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                                  )}
                                  <Link
                                    to={Constant.WORKSHEETEDIT.replace(
                                      ":id",
                                      worksheet.worksheetId
                                        ? worksheet.worksheetId.toString()
                                        : ""
                                    )}
                                    className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  >
                                    {worksheet.worksheetId}
                                  </Link>
                                </td>
                                <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                  <a
                                    href={Constant.WORKSHEETEDIT.replace(
                                      ":id",
                                      worksheet.worksheetId.toString()
                                    )}
                                  >
                                    {worksheet.title}
                                  </a>
                                </td>
                                <td
                                  className="px-3  pl-6 cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  onClick={() => {
                                    redirectToStandardDetail(
                                      worksheet.standardId
                                    );
                                  }}
                                >
                                  <a
                                    href={Constant.EDITSTANDARD.replace(
                                      ":id",
                                      worksheet.standardId.toString()
                                    )}
                                  >
                                    {worksheet.standardId}
                                  </a>
                                </td>
                                <td
                                  className="px-3  pl-6 cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  onClick={() => {
                                    redirectToStandardDetail(
                                      worksheet.standardId
                                    );
                                  }}
                                >
                                  <a
                                    href={Constant.EDITSTANDARD.replace(
                                      ":id",
                                      worksheet.standardId.toString()
                                    )}
                                  >
                                    {worksheet.standardCode}
                                  </a>
                                </td>
                                <td
                                  className="px-3  pl-6 cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  onClick={() => {
                                    redirectToStandardDetail(
                                      worksheet.standardId
                                    );
                                  }}
                                >
                                  <a
                                    href={Constant.EDITSTANDARD.replace(
                                      ":id",
                                      worksheet.standardId.toString()
                                    )}
                                  >
                                    {worksheet.standardName}
                                  </a>
                                </td>
                                <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                  <Tooltip text={worksheet?.description} />
                                </td>
                                <td className="px-3 py-2 capitalize  text-sm  text-left font-normal text-gray-500">
                                  <a
                                    title="Download Worksheet"
                                    className="cursor-pointer text-indigo-600"
                                    onClick={() =>
                                      downloadWS(
                                        worksheet.worksheetId,
                                        worksheet.worksheetFilePath
                                      )
                                    }
                                  >
                                    {worksheet.worksheetFilePath}
                                  </a>
                                </td>
                                <td className="px-3 py-2 capitalize  text-sm  text-left font-normal text-gray-500">
                                  <a
                                    title="Download Worksheet"
                                    className="cursor-pointer text-indigo-600"
                                    onClick={() =>
                                      downloadWS(
                                        worksheet.worksheetId,
                                        worksheet.worksheetKeyFilePath
                                      )
                                    }
                                  >
                                    {worksheet.worksheetKeyFilePath}
                                  </a>
                                </td>
                                <td className="flex justify-end items-center px-3 py-2 whitespace-wrap text-sm text-center font-normal text-gray-700">
                                  <div className="flex space-x-2 justify-center items-center">
                                    {worksheet.isApproved ? (
                                      <ApproveIcon />
                                    ) : (
                                      <Disapprove />
                                    )}

                                    <Menu
                                      as="div"
                                      className="relative flex justify-end items-center"
                                    >
                                      <Menu.Button className="h-7 bg-white inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bg-primary-color">
                                        <span className="sr-only">
                                          Open options
                                        </span>
                                        <DotsVerticalIcon
                                          className="w-5 h-5"
                                          aria-hidden="true"
                                        />
                                      </Menu.Button>
                                      <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                      >
                                        <Menu.Items className="mx-3 origin-top-right absolute right-7 top-0 w-48 mt-1 rounded-md shadow-lg z-10 bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 focus:outline-none">
                                          <AllowedTo
                                            perform={
                                              Permissions.worksheet_modify
                                            }
                                          >
                                            <div className="py-1">
                                              <Menu.Item>
                                                {({ active }) => (
                                                  <a
                                                    onClick={() =>
                                                      handleRedirect(
                                                        worksheet.worksheetId
                                                      )
                                                    }
                                                    className={classNames(
                                                      active
                                                        ? "bg-gray-100 text-gray-900"
                                                        : "text-gray-700",
                                                      "group flex items-center px-4 py-2 text-sm"
                                                    )}
                                                  >
                                                    <EditIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300" />
                                                    Edit
                                                  </a>
                                                )}
                                              </Menu.Item>
                                            </div>
                                          </AllowedTo>

                                          <AllowedTo
                                            perform={
                                              Permissions.worksheet_delete
                                            }
                                          >
                                            <div className="py-1">
                                              <Menu.Item>
                                                {({ active }) => (
                                                  <a
                                                    onClick={() =>
                                                      confirmDelete(
                                                        worksheet.worksheetId
                                                      )
                                                    }
                                                    className={classNames(
                                                      active
                                                        ? "bg-gray-100 text-gray-900"
                                                        : "text-gray-700",
                                                      "group flex items-center px-4 py-2 text-sm"
                                                    )}
                                                  >
                                                    <DeleteIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300" />
                                                    Delete
                                                  </a>
                                                )}
                                              </Menu.Item>
                                            </div>
                                          </AllowedTo>
                                        </Menu.Items>
                                      </Transition>
                                    </Menu>
                                  </div>
                                </td>
                              </tr>

                              <tr
                                className={
                                  index % 2 === 0 ? "bg-white" : "bg-gray-100"
                                }
                              >
                                <td className="pl-6 whitespace-wrap text-sm text-left font-normal text-gray-500 relative">
                                  {worksheet.isActive ? (
                                    <span className="mr-1 mb-2 h-full w-0.5 bg-green-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                                  ) : (
                                    <span className="mr-1 mb-2 h-full w-0.5 bg-red-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                                  )}
                                </td>
                                <td colSpan={8}>
                                  {worksheet?.worksheetTags?.map((tag) => (
                                    <li
                                      key={tag.tagId}
                                      className={`inline-block bg-[${
                                        tag.color
                                      }] 
                                rounded-full text-xs shadow-sm py-0.5 px-3  whitespace-nowrap mx-1 mb-2 cursor-pointer
                               ${
                                 TagColor.isDark(tag.color)
                                   ? "text-white"
                                   : "text-black"
                               }
                                `}
                                      onClick={() => {
                                        handleTagClick(tag);
                                      }}
                                    >
                                      {tag.name}
                                    </li>
                                  ))}
                                </td>
                              </tr>
                            </>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div
                  className={`flex justify-between items-center ${
                    worksheets.length === 0 ? "hidden" : ""
                  }`}
                >
                  <Pagination
                    pages={totalPages}
                    setPageNumber={setPageNumber}
                    ref={paginationRef}
                  />
                  <span>
                    <span className="text-sm pr-4 ml-auto text-left-700">
                      Showing {pageNumber} to{" "}
                      {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                      {totalRecords} results
                    </span>

                    <SelectPageSize
                      setPageSize={setPageSize}
                      type={"second"}
                      ref={pageSizeRef}
                    />
                  </span>
                </div>

                {worksheets.length === 0 && (
                  <p className="p-2 text-gray-500">No Records Found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};

export default Worksheets;
